import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import Flex from '~/common/components/Flex';
import Heading from '~/common/components/QuestionLibrary/components/Heading';
import AddIconButton from '~/common/components/QuestionLibrary/components/buttons/AddIconButton';
import DeleteIconButton from '~/common/components/QuestionLibrary/components/buttons/DeleteIconButton';
import {
  StyledTHead,
  StyledTable,
  StyledTd,
  StyledTr,
} from '~/common/components/QuestionLibrary/components/styledComponents/styledTableComponents';
import ConditionEvaluator from '~/common/components/QuestionLibrary/components/utilityComponents/ConditionEvaluator';
import QuestionResolutionWrapper from '~/common/components/QuestionLibrary/components/utilityComponents/QuestionResolutionWrapper';
import InputMapper from '~/common/components/QuestionLibrary/mappers/InputMapper';
import { useQuestionLibraryActions } from '~/common/components/QuestionLibrary/store/questionLibraryStore';
import formatValues from '~/common/components/QuestionLibrary/utils/formatValues';
import { validateField } from '~/common/components/QuestionLibrary/utils/utils';
import { Tbody, Tfoot, Tr } from '~/common/components/Tables';
import Text from '~/common/components/Texts/Text';

import FieldArrayInputContainer from './FieldArrayInputContainer';

type FieldArrayTableProps = {
  question?: Record<string, any>;
};

/**
 * @typedef {object} FieldArrayTableProps
 * @property {import('../../../types/complexQuestion').FieldArrayTableQuestionProps} question - field array table question
 *
 * @param {FieldArrayTableProps} props
 * @returns {JSX.Element}
 */

export default function FieldArrayTable({
  question,
}: Readonly<FieldArrayTableProps>) {
  const {
    name,
    id = 'fieldArrayTable',
    fieldArrayName,
    editableMode,
    hasBottomBorder = true,
    helperText,
    conditions,
    columns,
    title,
    appendItem,
    addButtonLabel = 'Add New Row',
    restrictions,
    isAddButtonEnabled = true,
    noBorder = false,
  } = question;
  const { register, setValue, trigger, watch } = useFormContext();
  const formFields = watch(fieldArrayName);
  const { setFocusedQuestion } = useQuestionLibraryActions();
  const [fieldArrayErrors, setFieldArrayErrors] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  const fields = useMemo(() => {
    return formFields ?? [];
  }, [formFields]);

  register(fieldArrayName, {
    validate: (value) => {
      const errors = validateFieldArray(value);
      setFieldArrayErrors(errors);
      return Object.keys(errors).length === 0;
    },
  });

  useEffect(() => {
    if (isHydrated) {
      if (!fields.length) {
        setIsHydrated(false);
      }
      return;
    }

    const fieldsWithIds = fields.map((field) => {
      return {
        ...field,
        _id: crypto.randomUUID(),
      };
    });
    setValue(fieldArrayName, fieldsWithIds);
    setIsHydrated(true);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [fields]);

  const showInput = (operation) => {
    switch (editableMode) {
      case 'always':
        return true;
      case 'onAdd':
        return operation !== null;
      case 'never':
        return false;
      default:
        return true;
    }
  };

  const handleAddNewRow = () => {
    const newRow = { ...appendItem, _id: crypto.randomUUID() };
    if (!newRow.operation) {
      newRow.operation = 'I';
    }
    setValue(fieldArrayName, [...fields, { ...newRow }]);
  };

  const handleRemoveRow = (index) => {
    clearRowErrors(index);
    setValue(
      fieldArrayName,
      fields.filter((_, i) => i !== index),
    );
  };

  const handleFieldChange = (index, column, value) => {
    const newFields = [...fields];
    newFields[index][column] = value;
    setValue(fieldArrayName, newFields);
  };

  const validateFieldArray = (fields) => {
    const errors = {};
    fields.forEach((field) => {
      columns.forEach((column) => {
        if (column.input) {
          const error = validateField(field[column.field], column.input.rules);
          if (error) {
            errors[field._id] = errors[field._id] || {};
            errors[field._id][column.field] = error;
          }
        }
      });
    });
    return errors;
  };

  const clearRowErrors = (index) => {
    const id = fields[index]._id;
    const currentErrors = { ...fieldArrayErrors };
    delete currentErrors[id];
    setFieldArrayErrors(currentErrors);
  };

  const hasError = (index, fieldName) => {
    const fieldErrors = getFieldError(index, fieldName);

    return !!fieldErrors;
  };

  const getFieldError = (index, fieldName) => {
    const rowErrors = fieldArrayErrors[index];
    if (!rowErrors) {
      return null;
    }
    const fieldError = rowErrors[fieldName];
    return fieldError;
  };

  const isAddDisabled = useMemo(() => {
    if (!restrictions) {
      return false;
    }
    const { maxRows } = restrictions;
    if (!maxRows) {
      return false;
    }

    return fields.length > maxRows - 1;
  }, [fields, restrictions]);

  return (
    <ConditionEvaluator conditions={conditions}>
      <QuestionResolutionWrapper
        resolutions={question.resolutions}
        hasBottomBorder={hasBottomBorder}
        id={id}
      >
        {title && (
          <Heading
            text={title}
            headingSize={'small'}
            id={`comparison-table-header-${name}`}
            placeholders={[]}
          />
        )}
        {helperText && <Text as="p">{helperText}</Text>}

        <StyledTable id={`${id}-table`} noBorder={noBorder}>
          <FieldArrayTableHeader columns={columns} />
          <Tbody>
            {fields.map((field, i) => (
              <StyledTr
                data-testid={`fieldArray-${fieldArrayName}-${i}`}
                key={field._id}
              >
                {columns.map((column) => (
                  <StyledTd
                    key={column.header}
                    textAlign={
                      ['currency'].includes(column.format?.type)
                        ? 'right'
                        : 'left'
                    }
                    width={column.format?.width ? column.format.width : 'auto'}
                  >
                    <Flex
                      direction={'column'}
                      align={
                        ['currency'].includes(column.format?.type)
                          ? 'flex-end'
                          : 'flex-start'
                      }
                    >
                      {(!column.input || !showInput(field.operation)) &&
                        column.field && (
                          <Text as={column.format?.as || 'span'}>
                            {formatValues(
                              column.format?.type,
                              field[column.field],
                            ) || field[column.field]}
                          </Text>
                        )}

                      {(!column.input || !showInput(field.operation)) &&
                        !column.field && (
                          <Text as={column.format?.as || 'span'}>
                            {column.defaultValue ?? '--'}
                          </Text>
                        )}

                      {column.input && showInput(field.operation) && (
                        <FieldArrayInputContainer
                          error={getFieldError(field._id, column.field)}
                        >
                          <InputMapper
                            width="240px"
                            hasError={hasError(field._id, column.field)}
                            inputType={column.input.type}
                            question={column.input}
                            field={{
                              value: field[column.field],
                              onFocus: () => {
                                setFocusedQuestion(field._id);
                              },
                              onChange: (e) => {
                                handleFieldChange(i, column.field, e);
                              },
                              onBlur: () => {
                                trigger(fieldArrayName);
                              },
                            }}
                          />
                        </FieldArrayInputContainer>
                      )}
                    </Flex>
                  </StyledTd>
                ))}
                <StyledTd width={'3rem'} padding="none" paddingTop={'1rem'}>
                  <Flex direction={'column'}>
                    {showInput(field.operation) && (
                      <DeleteIconButton
                        onClick={() => handleRemoveRow(i)}
                        testId={`${fieldArrayName}-remove-${i}`}
                      />
                    )}
                  </Flex>
                </StyledTd>
              </StyledTr>
            ))}
          </Tbody>
          {editableMode !== 'never' && isAddButtonEnabled && (
            <FieldArrayTableFooter
              handleAddNewRow={handleAddNewRow}
              addButtonLabel={addButtonLabel}
              isAddDisabled={isAddDisabled}
              fieldArrayName={fieldArrayName}
            />
          )}
        </StyledTable>
      </QuestionResolutionWrapper>
    </ConditionEvaluator>
  );
}

type FieldArrayTableFooterProps = {
  handleAddNewRow?: (...args: any[]) => any;
  addButtonLabel?: string;
  isAddDisabled?: boolean;
  fieldArrayName?: string;
};

function FieldArrayTableFooter({
  handleAddNewRow,
  addButtonLabel,
  isAddDisabled,
  fieldArrayName,
}: Readonly<FieldArrayTableFooterProps>) {
  return (
    <Tfoot>
      <Tr>
        <StyledTd padding="none">
          <AddIconButton
            handleClick={() => handleAddNewRow()}
            testId={`add-${fieldArrayName}`}
            isDisabled={isAddDisabled}
            label={addButtonLabel}
          />
        </StyledTd>
      </Tr>
    </Tfoot>
  );
}

type FieldArrayTableHeaderProps = {
  columns?: any[];
};

function FieldArrayTableHeader({
  columns,
}: Readonly<FieldArrayTableHeaderProps>) {
  return (
    <StyledTHead>
      <Tr>
        {columns.map((column) => (
          <StyledTd key={column.header} textAlign={'left'}>
            {column.header}
          </StyledTd>
        ))}
        <StyledTd></StyledTd>
      </Tr>
    </StyledTHead>
  );
}
